import React, { useState, useEffect } from 'react';
import { Drawer, DrawerHeader } from '@one-thd/sui-atomic-components';
import PropTypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import { AddToQuoteFormDrawer } from './AddToQuoteFormDrawer';
import { CreateNewQuoteDrawer } from './CreateNewQuoteDrawer';
import { SuccessDrawer } from './SuccessDrawer';
import { ErrorDrawer } from './ErrorDrawer';
import { SignInDrawer } from './SignInDrawer';
import { DrawerSkeleton } from '../shared/DrawerSkeleton';
import { useGetQuoteList } from '../hooks/useQuotes';
import { dataModel } from '../../dataModels/dataModel';
import { ProductInfo } from '../shared/ProductInfo';
import {
  DEFAULT_SELECTED_QUOTE,
  QUOTE_ACTIVE_STATUS,
  QUOTE_PENDING_STATUS,
  SCREENS,
  CREATE_A_QUOTE_BACK_BUTTON,
  CREATE_A_QUOTE_X_OR_CANCEL_BUTTON,
  ADD_TO_QUOTE_X_OR_CANCEL_BUTTON
} from '../shared/Constants';
import { useCustomerInformation } from '../hooks/useCustomerInformation';
import {
  triggerCloseQuoteAnalytics, triggerErrorAnalytics, getErrorMessage
} from '../../utils/helpers';

const AddToQuoteDrawer = ({
  location,
  drawerShowing,
  products,
  setDrawerShowing,
  projectQuoteData = {},
  productExceptions = [],
  quoteTimeTrackingRef
}) => {
  const [quotesPageSize, setQuotesPageSize] = useState(20);
  const [selectedQuote, setSelectedQuote] = useState(DEFAULT_SELECTED_QUOTE);
  const [error, setError] = useState(null);
  const [quoteName, setQuoteName] = useState('');
  const [quoteId, setQuoteId] = useState('');
  const [quoteList, setQuoteList] = useState([]);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [deliveryZip, setDeliveryZip] = useState(location.deliveryZip);
  const [quoteOriginAnalytic, setQuoteOriginAnalytic] = useState('');
  const [screen, setScreen] = useState(null);
  const customerInfo = useCustomerInformation();

  const {
    ADD_TO_QUOTE_FORM, CREATE_NEW_QUOTE, ERROR_SCREEN, SIGN_IN, SUCCESS
  } = SCREENS;

  const { projectId } = projectQuoteData || {};

  /**
   * This component will be consumed in different places, like buybox on PIP,
   * and product pod in BuyItAgain and Lists. There are differences
   * in the `QueryProvider` configuration between these places that change
   * `fetchPolicy`, so we need to set one explicity that works for us no matter
   * where we are.
   *
   * For example, when on a PLP-like page, the `QueryProvider` will typically be
   * configured with dataSource prop equal to `searchNav`, which will override
   * the default `fetchPolicy` from `cache-first` to `cache-only`. This does
   * not work for our `quoteList` call below since we need to make the
   * call at least once to get data, hence the explicit fetchPolicy=cache-first
   * to check cache first, then make a network call if necessary.
   */

  const { refetch, quoteData, quoteLoading, quoteError } = useGetQuoteList({
    ...customerInfo,
    pageSize: quotesPageSize,
    isGetQuote: true,
    projectId
  });
  const { totalNbrOfQuotes } = quoteData?.quoteList?.pagination || {};

  const isAccessTypeValid = (accessType) => {
    // hide if accessType on 1 or 2, show if null or not 1 or 2
    return !accessType || (accessType && accessType !== 1 && accessType !== 2);
  };

  const statusIsValid = (status) => {
    const upperCased = status.toUpperCase();
    return upperCased === QUOTE_ACTIVE_STATUS || upperCased === QUOTE_PENDING_STATUS;
  };

  const shouldShowItem = ({ status, accessType }) => {
    return statusIsValid(status) && isAccessTypeValid(accessType);
  };

  useEffect(() => {
    if (quotesPageSize > 20) {
      refetch();
    }
    const quotes = quoteData?.quoteList?.quotes || [];
    const filteredQuotes = quotes.filter(({ status, accessType }) => {
      return shouldShowItem({ status, accessType });
    }) || [];
    setQuoteList(filteredQuotes);
    const index = quotes.length;
    if (quotes[index - 1]?.status === 'Expired' || totalNbrOfQuotes === quoteList?.length) {
      setShowLoadMore(false);
    } else {
      setShowLoadMore(true);
    }
  }, [quotesPageSize, quoteData]);

  useEffect(() => {
    const quotes = quoteData?.quoteList?.quotes || [];
    const filteredQuotes = quotes.filter(({ status, accessType }) => {
      return shouldShowItem({ status, accessType });
    }) || [];
    if (drawerShowing && !quoteList.length && filteredQuotes.length) {
      setQuoteList(filteredQuotes);
    }
    const index = quotes.length - 1;
    if (quotes[index]?.status === 'Expired' || totalNbrOfQuotes === quoteList?.length) {
      setShowLoadMore(false);
    } else {
      setShowLoadMore(true);
    }
  }, [drawerShowing, quoteList, quoteData]);

  useEffect(() => {
    if (!quoteLoading && quoteError && /AUTH/.test(quoteError.message)) {
      triggerErrorAnalytics(quoteError);
      setScreen(SIGN_IN);
    }
    if (!quoteLoading && quoteError && !/AUTH/.test(quoteError.message)) {
      triggerErrorAnalytics(quoteError);
      setScreen(ERROR_SCREEN);
    }
    if (!quoteLoading && !quoteError && drawerShowing && quoteData) {
      if (totalNbrOfQuotes > 0 && screen !== SUCCESS) {
        setScreen(ADD_TO_QUOTE_FORM);
        window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-quote.addToQuoteOverlay');
      } else if (totalNbrOfQuotes === 0 && screen !== SUCCESS) {
        setScreen(CREATE_NEW_QUOTE);
        window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-quote.createAQuoteOverlay');
      }
    }
  }, [quoteData, quoteLoading, quoteError, drawerShowing]);

  useEffect(() => {
    if (screen === SUCCESS && location.deliveryZip !== deliveryZip) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-quote.deliveryZipCodeOverlay');
    }
  }, [screen]);

  const handleBackFn = () => {
    if (screen === CREATE_NEW_QUOTE && totalNbrOfQuotes > 0) {
      return () => {
        triggerCloseQuoteAnalytics(CREATE_A_QUOTE_BACK_BUTTON);
        setScreen(ADD_TO_QUOTE_FORM);
      };
    }
    return undefined;
  };

  const closeDrawer = () => {
    setDrawerShowing(false);
    setScreen(null);
    setQuoteName('');
    setSelectedQuote(DEFAULT_SELECTED_QUOTE);
    setQuoteOriginAnalytic('');
    const currentRefQuote = quoteTimeTrackingRef.current;
    currentRefQuote.startTime = null;
  };

  const handleCloseDrawer = () => {
    if (screen === ADD_TO_QUOTE_FORM) {
      triggerCloseQuoteAnalytics(ADD_TO_QUOTE_X_OR_CANCEL_BUTTON);
    }
    if (screen === CREATE_NEW_QUOTE) {
      triggerCloseQuoteAnalytics(CREATE_A_QUOTE_X_OR_CANCEL_BUTTON);
    }
    closeDrawer();
  };

  return (
    <Drawer
      data-testid="add-to-quote-drawer"
      open={drawerShowing}
      onClose={handleCloseDrawer}
      DrawerRootProps={{ 'data-component': 'AddToQuoteDrawer' }}
    >
      <DrawerHeader title="Add to Quote" onClose={handleCloseDrawer} onBack={handleBackFn()} />
      <div className="sui-flex sui-flex-col sui-min-h-screen">
        {quoteLoading ? (
          <DrawerSkeleton />
        ) : (
          <>
            {screen === ADD_TO_QUOTE_FORM && (
              <AddToQuoteFormDrawer
                location={{ localStoreId: location.localStoreId, deliveryZip, applianceDeliveryStore: location?.applianceDeliveryStore }}
                onClose={closeDrawer}
                products={products}
                quoteList={quoteList}
                quotesPageSize={quotesPageSize}
                selectedQuote={selectedQuote}
                setDeliveryZip={setDeliveryZip}
                setError={setError}
                setQuoteName={setQuoteName}
                setQuotesPageSize={setQuotesPageSize}
                setSelectedQuote={setSelectedQuote}
                showLoadMore={showLoadMore}
                projectQuoteData={projectQuoteData}
                productExceptions={productExceptions}
                setQuoteOriginAnalytic={setQuoteOriginAnalytic}
                setScreen={setScreen}
                quoteTimeTrackingRef={quoteTimeTrackingRef}
              />
            )}
            {screen === CREATE_NEW_QUOTE && (
              <CreateNewQuoteDrawer
                location={{ localStoreId: location.localStoreId, deliveryZip, applianceDeliveryStore: location?.applianceDeliveryStore }}
                onClose={closeDrawer}
                products={products}
                setError={setError}
                setQuoteId={setQuoteId}
                setQuoteName={setQuoteName}
                projectQuoteData={projectQuoteData}
                productExceptions={productExceptions}
                setQuoteOriginAnalytic={setQuoteOriginAnalytic}
                setDeliveryZip={setDeliveryZip}
                setScreen={setScreen}
                quoteTimeTrackingRef={quoteTimeTrackingRef}
                totalNbrOfQuotes={totalNbrOfQuotes}
              />
            )}
          </>
        )}
        {screen === SUCCESS && (
          <SuccessDrawer
            quoteOriginAnalytic={quoteOriginAnalytic}
            quoteName={quoteName}
            plural={products.length !== 1}
            onClose={closeDrawer}
            quoteId={quoteId || selectedQuote.quoteId}
            userId={selectedQuote.userId || customerInfo.userId}
            projectId={projectId}
            productExceptions={productExceptions}
          />
        )}
        {screen === SIGN_IN && <SignInDrawer onClose={closeDrawer} />}
        {screen === ERROR_SCREEN && <ErrorDrawer message={getErrorMessage(error)} onClose={closeDrawer} />}
      </div>
    </Drawer>
  );
};

AddToQuoteDrawer.propTypes = {
  location: PropTypes.shape({
    localStoreId: PropTypes.string,
    deliveryZip: PropTypes.string
  }).isRequired,
  drawerShowing: PropTypes.bool.isRequired,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      itemId: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      fulfillmentLocation: PropTypes.string.isRequired,
      fulfillmentMethod: PropTypes.string.isRequired,
      identifiers: PropTypes.shape({
        brandName: PropTypes.string,
        productLabel: PropTypes.string
      }).isRequired,
      pricing: PropTypes.shape({
        value: PropTypes.number
      }).isRequired,
      media: PropTypes.shape({
        images: PropTypes.arrayOf(
          PropTypes.shape({
            altText: PropTypes.string,
            sizes: PropTypes.arrayOf(PropTypes.string),
            subType: PropTypes.string,
            type: PropTypes.string,
            url: PropTypes.string
          })
        )
      })
    })
  ).isRequired,
  setDrawerShowing: PropTypes.func.isRequired,
  projectQuoteData: PropTypes.shape({
    projectId: PropTypes.string,
    projectZipCode: PropTypes.string,
    projectName: PropTypes.string
  }),
  productExceptions: PropTypes.arrayOf(
    PropTypes.shape({
      itemId: PropTypes.string,
      id: PropTypes.string,
      quantity: PropTypes.number,
      fulfillment: PropTypes.shape({}),
      identifiers: PropTypes.shape({}),
      pricing: PropTypes.shape({}),
      flag: PropTypes.string.isRequired
    })
  ),
  quoteTimeTrackingRef: PropTypes.shape({
    current: PropTypes.shape({
      startTime: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])])
    })
  }).isRequired
};

AddToQuoteDrawer.displayName = 'AddToQuoteDrawer';

AddToQuoteDrawer.dataModel = extend(dataModel, ProductInfo.dataModel);

export { AddToQuoteDrawer };
